import React, { Component, Suspense, lazy } from 'react';

import '@date-io/moment';
import { Switch, Redirect, withRouter, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import theme from './theme';
import { CookiesProvider } from 'react-cookie';
import { track } from './shared/Tracking';
import ApiClient from './shared/ApiClient';
import './fonts.css';

import { UserProvider, UserContext } from './UserContext';
import { FeatureFlagProvider } from 'shared/FeatureFlagContext';
import { ModalsContextProvider } from './ModalsContext';
import { NotificationContextProvider } from './parent-portal/notifications/NotificationContext/NotificationContext';
import ReactGA from 'react-ga4';
import { CareersBaseUrl, Environment, GoogleAnalytics, StripeKey } from 'environmentVariables';
import { AdminAppContextProvider, AdminAppContext } from 'internal/shared/AdminAppContext';
import { Public } from './shared/Public';
import { PublicOrBusiness } from './shared/PublicOrBusiness';
import { Private } from './shared/Private';
import HttpsRedirect from 'react-https-redirect';
import ZipSearch from './parent-portal/public/MarketingSite/home/Home';
import Listings from './parent-portal/public/MarketingSite/Jobs/Listings';
import SignUpPage from './parent-portal/signup/SignUpPage';
import LoginPage from './parent-portal/public/LoginPage';

const VerifyMfa = lazy(() => import('./parent-portal/login/VerifyMfa'));
const ChangePassword = lazy(() => import('./parent-portal/login/ChangePassword'));
const VerificationCode = lazy(() => import('./parent-portal/login/VerificationCode'));
import Home from './parent-portal/home/new/Dashboard';
const PublicFooter = lazy(() => import('./parent-portal/shared/PublicFooter'));
const ChatAndHelpCenter = lazy(() => import('./parent-portal/shared/ChatAndHelpCenter'));
const SearchHelpCenter = lazy(() => import('./parent-portal/shared/SearchHelpCenter'));
import ManageCenters from './parent-portal/manage-centers/ManageCenters';
import ManageUsers from './parent-portal/manage-users/ManageUsers';
import Messages from './parent-portal/messages/Messages';
import UpcomingJobsPage from './parent-portal/jobs/upcoming/MyJobsPage';
import Payments from './parent-portal/payments/Payments';
import Providers from './parent-portal/sitters/Providers';
import Timesheets from 'parent-portal/timesheets/Timesheets';

const ProviderProfile = lazy(() => import('./parent-portal/provider-profile/ProviderProfile'));
const BusinessProfile = lazy(() => import('./parent-portal/profile/BusinessProfile/BusinessProfile'));
const NotificationPage = lazy(() => import('parent-portal/notifications/NotificationPage/NotificationPage'));
const ReviewProvider = lazy(() => import('parent-portal/provider-reviews/ReviewProvider'));
const ConfirmEmploymentHistory = lazy(
    () => import('./sitter-portal/confirm-employment-history/ConfirmEmploymentHistory'),
);
const ApplyToJob = lazy(() => import('./parent-portal/signup/ApplyToJob'));
const RecruitPublicRoutes = lazy(() => import('./recruit/RecruitPublicRoutes'));

const IS_CAREERS_ROOT_HOST = (() => {
    try {
        const careersUrl = new URL(CareersBaseUrl || '');
        const sameHost = window.location.hostname === careersUrl.hostname;
        const rootPath = !careersUrl.pathname || careersUrl.pathname === '/';
        return sameHost && rootPath;
    } catch {
        return false;
    }
})();
const ApplySuccess = lazy(() => import('./parent-portal/signup/ApplySuccess'));
const DownloadShiftsAttachments = lazy(() => import('parent-portal/jobs/upcoming/DownloadShiftsAttachments'));
const SitterLogin = lazy(() => import('./sitter-portal/login/Login'));
const ProvideWorkerReference = lazy(() => import('./sitter-portal/provide-worker-reference/ProvideWorkerReference'));
const NotFound = lazy(() => import('./internal/shared/NotFound'));
const EmaiVerificationPage = lazy(() => import('./parent-portal/public/EmailVerificationPage'));
const UniversalLinkingPage = lazy(() => import('./parent-portal/public/UniversalLinkingPage'));
const RedirectToAppJobDetails = lazy(() => import('./parent-portal/public/RedirectToAppJobDetails'));
const ProviderRedirect = lazy(() => import('parent-portal/provider-profile/ProviderRedirect'));
const UnsupportedRegionPage = lazy(
    () => import('parent-portal/blockers/SupportedRegions/UnsupportedRegion/UnsupportedRegionPage'),
);
const OutOfCountryPage = lazy(() => import('parent-portal/blockers/WAF/OutOfCountryPage'));
const HotConversationNotifications = lazy(() => import('internal/texting/outreach/HotConversationNotifications'));
const DraggablePhoneDialog = lazy(() => import('internal/business-locations/DraggablePhoneDialog'));
const DraggableNotesDialog = lazy(() => import('internal/shared/tables/DraggableNotesDialog'));
const RecruitBusinessRoutes = lazy(() => import('./recruit/RecruitBusinessRoutes'));
const VerifySenderEmailPage = lazy(() =>
    import('./recruit/pages/business').then((module) => ({ default: module.VerifySenderEmailPage })),
);
const RecruitCandidateRoutes = lazy(() => import('./recruit/RecruitCandidateRoutes'));
const FamilyLetter = lazy(() => import('parent-portal/home/FamilyLetter'));
const AdminDashboard = lazy(() => import('./internal/home/AdminDashboard'));
const DailyApplicantFeed = lazy(() => import('./internal/applicants/DailyApplicantFeed'));
const PushNotificationScreen = lazy(() => import('./internal/pushnotification/PushNotificationScreen'));
const Fees = lazy(() => import('parent-portal/shared/Footer/Fees'));
const IndeedJobListing = lazy(() => import('internal/outreach/indeed/IndeedJobListing'));
const AutobookHistoryPage = lazy(() => import('internal/booking/AutobookHistoryPage'));
const HowToDeleteAcct = lazy(() => import('parent-portal/public/HowToDeleteAcct'));
const MassTextScreen = lazy(() => import('./internal/mass-text/MassTextScreen'));
const MassEmailScreen = lazy(() => import('./internal/mass-email/MassEmailScreen'));
const Impersonate = lazy(() => import('./internal/impersonate/Impersonate'));
const SendEmail = lazy(() => import('internal/outreach/email/SendEmail'));
const ClockInQRCode = lazy(() => import('parent-portal/payments/Components/business/ClockInQRCode'));
const TeacherShowcasePage = lazy(() => import('parent-portal/home/TeacherShowcasePage'));
const BusinessLocationPage = lazy(() => import('parent-portal/BusinessLocation/BusinessLocationPage'));
const BusinessJob = lazy(() => import('parent-portal/jobs/upcoming/BusinessJob/BusinessJob'));
const BusinessApplicantsApproval = lazy(
    () => import('parent-portal/jobs/upcoming/BusinessJob/BusinessApplicantsApproval'),
);
const SubmitReport = lazy(() => import('parent-portal/reports/SubmitReport'));
const PrivacyPolicy = lazy(() => import('parent-portal/shared/Footer/PrivacyPolicy'));
const ProviderTermsOfUse = lazy(() => import('parent-portal/shared/Footer/ProviderTermsOfUse'));
const CenterTermsOfUse = lazy(() => import('parent-portal/shared/Footer/CenterTermsOfUse'));
const FamilyTermsOfUse = lazy(() => import('parent-portal/shared/Footer/FamilyTermsOfUse'));
const TermsOfUse = lazy(() => import('parent-portal/shared/Footer/TermsOfUse'));
const AdminPaymentsDashboard = lazy(() => import('./internal/payments/Dashboard'));
const HoursReported = lazy(() => import('./internal/payments/HoursReported'));
const Customer = lazy(() => import('./internal/payments/Customer'));
const Customers = lazy(() => import('./internal/payments/Customers'));
const Discount = lazy(() => import('./internal/payments/Discount'));
const Discounts = lazy(() => import('./internal/payments/Discounts'));
const Invoice = lazy(() => import('./internal/payments/Invoice'));
const Invoices = lazy(() => import('./internal/payments/Invoices'));
const PayStub = lazy(() => import('./internal/payments/PayStub'));
const PayStubs = lazy(() => import('./internal/payments/PayStubs'));
const PQWebhooks = lazy(() => import('./internal/payments/PQWebhooks'));
const Upload1099Users = lazy(() => import('./internal/payments/Upload1099Users'));
const SubPoolMemberUpload = lazy(() => import('./internal/sub-pool/MemberUpload'));
const CreateSubPool = lazy(() => import('./internal/sub-pool/CreateSubPool'));
const SubPoolDashboards = lazy(() => import('./internal/sub-pool/SubPoolDashboards'));
const StakeholderAcceptInvite = lazy(() => import('./internal/sub-pool-stakeholder/StakeholderAcceptInvite'));
const StakeholderDashboard = lazy(() => import('./internal/sub-pool-stakeholder/StakeholderDashboard'));
const ManageStakeholders = lazy(() => import('./internal/sub-pool-stakeholder/ManageStakeholders'));
const AdminMessageTable = lazy(() => import('./internal/texting/Messages'));
const BusinessMessageTable = lazy(() => import('./internal/texting/BusinessMessageTable'));
const NewOutreachView = lazy(() => import('./internal/texting/NewOutreachView'));
const AdminMessenger = lazy(() => import('./internal/messenger'));
const Advances = lazy(() => import('./internal/payments/Advances'));
const AdvanceDetails = lazy(() => import('./internal/payments/AdvanceDetails'));
const ApproveHours = lazy(() => import('./internal/payments/ApproveHours'));
const PayProvider = lazy(() => import('./internal/payments/PayProvider'));
const EmailOutreach = lazy(() => import('./internal/outreach/email/EmailOutreach'));
const AdminMessages = lazy(() => import('./internal/texting/AdminMessages'));
const BlockedPayStubs = lazy(() => import('./internal/payments/BlockedPayStubs'));
const BlockedInvoices = lazy(() => import('./internal/payments/BlockedInvoices'));
const StripeReviews = lazy(() => import('./internal/payments/StripeReviews'));
const StripeReviewDetails = lazy(() => import('./internal/payments/StripeReviewDetails'));
const ApplicantApproval = lazy(() => import('./internal/applicants/ApplicantApproval'));
const ShareProvider = lazy(() => import('./internal/providers/ShareProvider'));
const ManageScheduledJobs = lazy(() => import('./internal/providers/ManageScheduledJobs'));
const ProviderDocumentUpload = lazy(() => import('./internal/document-verify/ProviderDocumentUpload'));
const Businesses = lazy(() => import('./internal/business/Businesses'));
const BusinessDownload = lazy(() => import('./internal/business/BusinessDownload'));
const RegionMap = lazy(() => import('./internal/business/RegionMap'));
const BusinessMap = lazy(() => import('./internal/business/BusinessMap'));
const ProviderMap = lazy(() => import('./internal/business/ProviderMap'));
const VettingPipeline = lazy(() => import('./internal/vetting-pipeline/VettingPipeline'));
const WorkerPipeline = lazy(() => import('./internal/worker-pipeline/WorkerPipeline'));
const HighlightJobs = lazy(() => import('internal/business/HighlightJobs'));
const SalesLeads = lazy(() => import('internal/salesLeads/SalesLeads'));
const Substitutes = lazy(() => import('internal/substitutes/Substitutes'));
const VerifyDocumentsPage = lazy(() => import('internal/document-verify/VerifyDocumentsPage'));
const IndeedJobPost = lazy(() => import('internal/outreach/indeed/IndeedJobPost'));
const RideShares = lazy(() => import('internal/rideshares'));
const Demo = lazy(() => import('internal/demo/Demo'));
const Provider = lazy(() => import('internal/providers/Provider'));
const BusinessLocation = lazy(() => import('internal/business-location/BusinessLocation'));
const BusinessLocations = lazy(() => import('internal/business-locations/BusinessLocations'));
const CombineLocations = lazy(() => import('internal/combine-locations/CombineLocations'));
const Disputes = lazy(() => import('internal/disputes/Disputes'));
const GmailAuth = lazy(() => import('internal/gmail-auth/GmailAuth'));
const TasksPage = lazy(() => import('internal/TaskList/TasksPage'));
const ViewQualifications = lazy(() => import('internal/qualification/ViewQualifications'));
const ChatwootContactPage = lazy(() => import('internal/support/ChatwootContactPage'));
const ChatwootAutomations = lazy(() => import('internal/support/ChatwootAutomations'));
const ChatwootContactLookup = lazy(() => import('internal/support/ChatwootContactLookup'));
const HourReports = lazy(() => import('internal/payments/HourReports'));
const HourReport = lazy(() => import('internal/payments/HourReport'));
const JobHistoryPage = lazy(() => import('internal/job-history/JobHistoryPage'));
const WorkerMatching = lazy(() => import('internal/substitutes/Matching/ReverseMatching/WorkerMatching'));
const WorkerCompliance = lazy(() => import('internal/worker-compliance/WorkerCompliance'));
const FileBackgroundCheck = lazy(() => import('internal/background-check/FileBackgroundCheck'));
const HealthCheckPage = lazy(() => import('internal/health-check/HealthCheckPage'));
const Dependability = lazy(() => import('internal/dependability/Dependability'));
const FirstAidCprClasses = lazy(() => import('./internal/first-aid-cpr/FirstAidCprClasses'));
const MedicalProvidersAdminPage = lazy(() => import('internal/medical-provider/MedicalProvidersAdminPage'));
const MedicalProviderPage = lazy(() => import('internal/medical-provider/MedicalProviderPage'));
const AccountWarnings = lazy(() => import('internal/account-warnings/AccountWarnings'));
const IncidentReports = lazy(() => import('internal/incident-reports/IncidentReports'));
const IndeedAdmin = lazy(() => import('internal/indeed-admin/IndeedAdmin'));
const IndeedAnalytics = lazy(() => import('internal/indeed-analytics/IndeedAnalytics'));
const RedemptionCodes = lazy(() => import('internal/redemption-codes/RedemptionCodes'));
const ColorPalette = lazy(() => import('internal/color-palette/ColorPalette'));

const RecruitDocuments = lazy(() => import('internal/recruit/RecruitDocuments'));
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    static getDerivedStateFromError(_error) {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <NotFound error={true} />;
        }
        return this.props.children;
    }
}

const Authorization = (allowedRoles) => (WrappedComponent) =>
    class WithAuthorization extends React.Component {
        render() {
            return (
                <UserContext.Consumer>
                    {(context) => {
                        const role = context.role;
                        const user = context.user;
                        if (role) {
                            if (allowedRoles.includes(role)) {
                                if (role === 'admin' || role === 'medical_provider') {
                                    return (
                                        <AdminAppContextProvider>
                                            <AdminAppContext.Consumer>
                                                {(adminContext) => {
                                                    const { phoneDialogProps, notesDialogProps } = adminContext;
                                                    return (
                                                        <>
                                                            <DraggableNotesDialog {...notesDialogProps} />
                                                            <DraggablePhoneDialog {...phoneDialogProps} />
                                                            <WrappedComponent {...this.props} />
                                                            <HotConversationNotifications />
                                                        </>
                                                    );
                                                }}
                                            </AdminAppContext.Consumer>
                                        </AdminAppContextProvider>
                                    );
                                } else if (role === 'business' || role === 'business_active') {
                                    return (
                                        <>
                                            <WrappedComponent {...this.props} />
                                            <ChatAndHelpCenter />
                                            <SearchHelpCenter />
                                            <PublicFooter />
                                        </>
                                    );
                                } else if (role === 'family') {
                                    return <WrappedComponent {...this.props} />;
                                } else if (role === 'marketer' || role === 'sitter') {
                                    return <WrappedComponent {...this.props} />;
                                } else if (role === 'sub_pool_stakeholder') {
                                    return <WrappedComponent {...this.props} />;
                                } else {
                                    if (role === 'admin') {
                                        return <Redirect to="/adminperson/business" />;
                                    } else if (role === 'marketer') {
                                        return <Redirect to="/adminperson/outreach/directory/add" />;
                                    } else if (role === 'medical_provider') {
                                        const url = `/medical-provider/${user?.id}`;
                                        return <Redirect to={url} />;
                                    }
                                    return <NotFound error={false} />;
                                }
                            } else {
                                return null;
                            }
                        }
                    }}
                </UserContext.Consumer>
            );
        }
    };

const Business = Authorization(['business', 'business_active']);
const Family = Authorization(['family']);
const Admin = Authorization(['admin']);
const MedicalProviderAuth = Authorization(['medical_provider', 'admin']);
const Stakeholder = Authorization(['sub_pool_stakeholder']);

function attemptOtpLogin(otp, path) {
    return ApiClient(`api/rest-auth/otp/?otp=${otp}&path=${path}`)
        .then((response) => {
            localStorage.setItem('token', response.key);
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error);
        });
}

const stripe = loadStripe(StripeKey);

class App extends Component {
    constructor(props) {
        super(props);
    }

    initializeGoogle = () => {
        if (Environment === 'production') {
            const ga = GoogleAnalytics;
            if (ga) {
                ReactGA.initialize(ga);
            }
        }
    };

    componentDidMount = async () => {
        this.initializeGoogle();
        this.token = localStorage.getItem('token');

        if (window.location.href.indexOf('/#/') > -1) {
            window.location = window.location.href.replace('/#/', '/');
            return;
        }

        this.originalLocation = this.props.history.location;
        const fromLocation = this.props.history.location?.state?.from;
        const queryParams = new URLSearchParams(this.originalLocation.search);
        const isRecruitCandidatePath = this.originalLocation.pathname.startsWith('/recruit-candidate');
        if (!this.token && queryParams.has('otp') && !isRecruitCandidatePath) {
            await attemptOtpLogin(queryParams.get('otp'), fromLocation?.pathname);
            this.token = localStorage.getItem('token');

            queryParams.delete('otp');
            if (fromLocation) {
                const queryStr = queryParams.toString();
                const newPath = queryStr ? `${fromLocation.pathname}?${queryStr}` : fromLocation.pathname;
                this.props.history.replace(newPath);
            } else {
                this.props.history.push(`${this.originalLocation.pathname}?${queryParams.toString()}`);
            }
            window.location.reload();
        }

        track('Visted Page', {
            pageName: this.props.history.location.pathname,
        });

        if (Environment === 'production') {
            ReactGA.send({ hitType: 'pageview', page: this.props.history.location.pathname });
        }

        let screenTimer = Date.now();
        let currentPage = this.props.history.location.pathname;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.unlisten = this.props.history.listen((location, _action) => {
            track('Visted Page', {
                pageName: location.pathname,
            });

            track('Time Spent On Page', {
                pageName: currentPage,
                duration: (Date.now() - screenTimer) / 1000,
            });

            currentPage = location.pathname;
            screenTimer = Date.now();
        });
    };

    componentWillUnmount() {
        this.unlisten();
    }

    everything() {
        return (
            <UserProvider>
                <FeatureFlagProvider>
                    <ModalsContextProvider>
                        <NotificationContextProvider>
                            <Elements stripe={stripe}>
                                <ThemeProvider theme={theme}>
                                    <CookiesProvider>
                                        <HttpsRedirect>
                                            <ErrorBoundary>
                                                <Suspense fallback={<div>Loading...</div>}>
                                                    <Switch>
                                                        <Private
                                                            exact
                                                            path="/adminperson/dashboard"
                                                            component={Admin(AdminDashboard)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/impersonate"
                                                            component={Admin(Impersonate)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/applicant-approval"
                                                            component={Admin(ApplicantApproval)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/daily-applicant-feed"
                                                            component={Admin(DailyApplicantFeed)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/share-provider"
                                                            component={Admin(ShareProvider)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/scheduled-jobs/:user_id"
                                                            component={Admin(ManageScheduledJobs)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/upload-documents/:user_id"
                                                            component={Admin(ProviderDocumentUpload)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/dashboard"
                                                            component={Admin(AdminPaymentsDashboard)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/hours-reported"
                                                            component={Admin(HoursReported)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/customers"
                                                            component={Admin(Customers)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/customers/:id"
                                                            component={Admin(Customer)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/discounts"
                                                            component={Admin(Discounts)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/discounts/:id"
                                                            component={Admin(Discount)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/invoices"
                                                            component={Admin(Invoices)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/invoices/:id"
                                                            component={Admin(Invoice)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/reviews"
                                                            component={Admin(StripeReviews)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/reviews/:id"
                                                            component={Admin(StripeReviewDetails)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/blocked/invoices"
                                                            component={Admin(BlockedInvoices)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/blocked/paystubs"
                                                            component={Admin(BlockedPayStubs)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/paystubs"
                                                            component={Admin(PayStubs)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/paystubs/:id"
                                                            component={Admin(PayStub)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/approve-hours"
                                                            component={Admin(ApproveHours)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/pq-hooks"
                                                            component={Admin(PQWebhooks)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/hour-reports"
                                                            component={Admin(HourReports)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/hour-reports/:id"
                                                            component={Admin(HourReport)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/upload-1099"
                                                            component={Admin(Upload1099Users)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/sub-pool/upload-members"
                                                            component={Admin(SubPoolMemberUpload)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/sub-pool/create"
                                                            component={Admin(CreateSubPool)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/sub-pool/stakeholders"
                                                            component={Admin(ManageStakeholders)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/sub-pool/dashboards"
                                                            component={Admin(SubPoolDashboards)}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/sub-pool-stakeholder/accept-invite/:token"
                                                            component={StakeholderAcceptInvite}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/sub-pool-stakeholder/dashboard"
                                                            component={Stakeholder(StakeholderDashboard)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/messages"
                                                            component={Admin(AdminMessages)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/push-notifications"
                                                            component={Admin(PushNotificationScreen)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/mass-text"
                                                            component={Admin(MassTextScreen)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/mass-email"
                                                            component={Admin(MassEmailScreen)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/message-table"
                                                            component={Admin(AdminMessageTable)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/business-message-table"
                                                            component={Admin(BusinessMessageTable)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/outreach/email"
                                                            component={Admin(EmailOutreach)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/outreach-new"
                                                            component={Admin(NewOutreachView)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/first-aid-cpr-classes"
                                                            component={Admin(FirstAidCprClasses)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/medical-providers"
                                                            component={Admin(MedicalProvidersAdminPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/medical-provider/:id"
                                                            component={MedicalProviderAuth(MedicalProviderPage)}
                                                        />

                                                        <Private
                                                            exact
                                                            path="/adminperson/messenger"
                                                            component={Admin(AdminMessenger)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/email"
                                                            component={Admin(SendEmail)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/advances"
                                                            component={Admin(Advances)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/advances/:id"
                                                            component={Admin(AdvanceDetails)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/payments/pay-provider/"
                                                            component={Admin(PayProvider)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/businesses"
                                                            component={Admin(Businesses)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/businesses/download"
                                                            component={Admin(BusinessDownload)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/documents-verify"
                                                            component={Admin(VerifyDocumentsPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/recruit-documents"
                                                            component={Admin(RecruitDocuments)}
                                                        />

                                                        <Private
                                                            exact
                                                            path="/adminperson/region-map"
                                                            component={Admin(RegionMap)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/biz-map"
                                                            component={Admin(BusinessMap)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/provider-map"
                                                            component={Admin(ProviderMap)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/vetting-pipeline"
                                                            component={Admin(VettingPipeline)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/worker-pipeline"
                                                            component={Admin(WorkerPipeline)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/highlight-jobs"
                                                            component={Admin(HighlightJobs)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/sales-leads"
                                                            component={Admin(SalesLeads)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/substitutes"
                                                            component={Admin(Substitutes)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/disputes"
                                                            component={Admin(Disputes)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/rideshares"
                                                            component={Admin(RideShares)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/cw-contact-lookup"
                                                            component={Admin(ChatwootContactLookup)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/outreach/indeed-post"
                                                            component={Admin(IndeedJobPost)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/autobook-history"
                                                            component={AutobookHistoryPage}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/provider/:id"
                                                            component={Admin(Provider)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/business-location/:id"
                                                            component={Admin(BusinessLocation)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/business-locations"
                                                            component={Admin(BusinessLocations)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/combine-locations"
                                                            component={Admin(CombineLocations)}
                                                        />

                                                        <Private
                                                            exact
                                                            path="/adminperson/gmail-auth"
                                                            component={Admin(GmailAuth)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/gsync/callback"
                                                            component={Admin(GmailAuth)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/qualifications"
                                                            component={Admin(ViewQualifications)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/chatwoot/contacts"
                                                            component={Admin(ChatwootContactPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/chatwoot/automations"
                                                            component={Admin(ChatwootAutomations)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/dependability"
                                                            component={Admin(Dependability)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/color-palette"
                                                            component={Admin(ColorPalette)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/job-history/:id"
                                                            component={Admin(JobHistoryPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/worker-matching"
                                                            component={Admin(WorkerMatching)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/worker-compliance"
                                                            component={Admin(WorkerCompliance)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/file-background-check"
                                                            component={FileBackgroundCheck}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/tasks"
                                                            component={Admin(TasksPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/account-warnings"
                                                            component={Admin(AccountWarnings)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/incident-reports"
                                                            component={Admin(IncidentReports)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/indeed"
                                                            component={Admin(IndeedAdmin)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/indeed-analytics"
                                                            component={Admin(IndeedAnalytics)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/redemption-codes"
                                                            component={Admin(RedemptionCodes)}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/indeed-job/:id"
                                                            component={IndeedJobListing}
                                                        />
                                                        <Private exact path="/request">
                                                            {<Redirect to="/home" />}
                                                        </Private>
                                                        <Private exact path="/home" component={Business(Home)} />
                                                        <Private
                                                            exact
                                                            path="/profile"
                                                            component={Business(BusinessProfile)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/manage-centers"
                                                            component={Business(ManageCenters)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/manage-users"
                                                            component={Business(ManageUsers)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/profile/locations/:id"
                                                            component={Business(BusinessLocationPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/providers/view/:id"
                                                            component={Business(ProviderRedirect)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/providers/view/"
                                                            component={Business(ProviderRedirect)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/providers/review/:id"
                                                            component={Business(ReviewProvider)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/provider/:id"
                                                            component={Business(ProviderProfile)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/provider"
                                                            component={Business(ProviderProfile)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/payments"
                                                            component={Business(Payments)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/timesheets"
                                                            component={Business(Timesheets)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/jobs"
                                                            component={Business(UpcomingJobsPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/jobs/:id"
                                                            component={Business(BusinessJob)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/jobs/:id/applicants"
                                                            component={Business(BusinessApplicantsApproval)}
                                                        />

                                                        <Private
                                                            exact
                                                            path="/download-shifts-attachments"
                                                            component={Business(DownloadShiftsAttachments)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/messages"
                                                            component={Business(Messages)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/messages/:id"
                                                            component={Business(Messages)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/providers"
                                                            component={Business(Providers)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/providers/preview/:id?"
                                                            component={Business()}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/clock-in"
                                                            component={Business(ClockInQRCode)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/teacher-showcase"
                                                            component={Business(TeacherShowcasePage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/reports/submit"
                                                            component={SubmitReport}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/signin/change/password/verify"
                                                            component={VerificationCode}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/signin/create/password/verify"
                                                            component={VerificationCode}
                                                        />
                                                        <Public exact path="/login/verify" component={VerifyMfa} />
                                                        <Public
                                                            exact
                                                            path="/signin/change/password"
                                                            component={ChangePassword}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/notifications"
                                                            component={Business(NotificationPage)}
                                                        />
                                                        <PublicOrBusiness
                                                            exact
                                                            path="/recruit/settings/emails/verify"
                                                            component={VerifySenderEmailPage}
                                                        />
                                                        <Private
                                                            path="/recruit"
                                                            component={Business(RecruitBusinessRoutes)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/adminperson/health-check"
                                                            component={Admin(HealthCheckPage)}
                                                        />
                                                        <Private
                                                            exact
                                                            path="/family"
                                                            component={Family(FamilyLetter)}
                                                        />
                                                        <Public exact path="/sitter/signin">
                                                            {localStorage.getItem('token') === null ? (
                                                                <SitterLogin />
                                                            ) : (
                                                                <Redirect to="/sitter/payment" />
                                                            )}
                                                        </Public>
                                                        <Public exact path="/sitter">
                                                            {localStorage.getItem('token') === null ? (
                                                                <SitterLogin />
                                                            ) : (
                                                                <Redirect to="/sitter/payment" />
                                                            )}
                                                        </Public>
                                                        <Public
                                                            exact
                                                            path="/verify-email/:id"
                                                            component={EmaiVerificationPage}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/provide-reference/:id"
                                                            component={ProvideWorkerReference}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/confirm-employment/:id"
                                                            component={ConfirmEmploymentHistory}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/gotandem/"
                                                            component={UniversalLinkingPage}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/gotandem/:id"
                                                            component={RedirectToAppJobDetails}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/privacy-policy"
                                                            component={PrivacyPolicy}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/tandem-terms-of-use"
                                                            component={TermsOfUse}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/tandem-terms-of-use/provider"
                                                            component={ProviderTermsOfUse}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/tandem-terms-of-use/center"
                                                            component={CenterTermsOfUse}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/tandem-terms-of-use/family"
                                                            component={FamilyTermsOfUse}
                                                        />
                                                        <Public exact path="/tandem-pay/fees" component={Fees} />
                                                        <Route path="/careers" component={RecruitPublicRoutes} />
                                                        <Route
                                                            path="/recruit-candidate"
                                                            component={RecruitCandidateRoutes}
                                                        />
                                                        <Public exact path="/listings" component={Listings} />
                                                        <Public
                                                            exact
                                                            path="/application/apply/:id"
                                                            component={ApplyToJob}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/application/success"
                                                            component={ApplySuccess}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/delete-account"
                                                            component={HowToDeleteAcct}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/unsupportedregion"
                                                            component={UnsupportedRegionPage}
                                                        />
                                                        <Public
                                                            exact
                                                            path="/out-of-country"
                                                            component={OutOfCountryPage}
                                                        />
                                                        <Public exact path="/worker-search" component={ZipSearch} />
                                                        {!localStorage.getItem('token') ? (
                                                            <Public exact path="/signup" component={SignUpPage} />
                                                        ) : (
                                                            <Public exact path="/signup" component={<></>}>
                                                                <Redirect to="/home" />
                                                            </Public>
                                                        )}
                                                        {!localStorage.getItem('token') ? (
                                                            <Public exact path="/login" component={LoginPage} />
                                                        ) : (
                                                            <Public exact path="/login" component={<></>}>
                                                                <Redirect to="/home" />
                                                            </Public>
                                                        )}
                                                        {IS_CAREERS_ROOT_HOST ? (
                                                            <Route path="/" component={RecruitPublicRoutes} />
                                                        ) : !localStorage.getItem('token') ? (
                                                            <Public exact path="/" component={SignUpPage} />
                                                        ) : (
                                                            <Public exact path="/" component={<></>}>
                                                                <Redirect to="/home" />
                                                            </Public>
                                                        )}
                                                        <Public exact path="/adminperson">
                                                            {!localStorage.getItem('token') ? (
                                                                <Redirect to="/adminperson/signin" />
                                                            ) : (
                                                                <Redirect to="/adminperson/businesses" />
                                                            )}
                                                        </Public>
                                                        <Private
                                                            exact
                                                            path="/adminperson/demo"
                                                            component={Admin(Demo)}
                                                        />
                                                        <Public component={() => <NotFound error={false} />} />
                                                    </Switch>
                                                </Suspense>
                                            </ErrorBoundary>
                                        </HttpsRedirect>
                                    </CookiesProvider>
                                </ThemeProvider>
                            </Elements>
                        </NotificationContextProvider>
                    </ModalsContextProvider>
                </FeatureFlagProvider>
            </UserProvider>
        );
    }

    render() {
        return <>{this.everything()}</>;
    }
}

export default withRouter(App);
